import { useEffect, forwardRef } from 'react';
import { FormPart } from './index';
import { isTouchDevice } from 'helpers/deviceDetection';
import { useTheme } from 'hooks/useTheme';
import { useToggle } from 'hooks/useToggle';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import styled from 'styled-components';
import { getThemedInputColor } from 'helpers/colors';

export interface IInputProps {
  onClick?: (e: React.SyntheticEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  errorText?: string;
  handleBlur?: (e: FocusEvent) => void;
  ref?: any;
  withEye?: boolean;
  icon?: React.ReactNode;
  type: string;
  maxLength?: number;
}

const InputElement = styled.input<IInputProps>`
  border: 1px solid ${(props) => props.theme.colors.inputBorderColor};
  box-shadow: none;
  color: ${({ theme }) => getThemedInputColor(theme.colors.inputColor)};
  background: ${(props) => props.theme.colors.inputBg};
  box-sizing: border-box;
  padding: 0 20px;
  border-radius: ${(props) => props.theme.inputBorderRadius}px;
  display: inline-flex;
  height: 45px;
  appearance: auto;
  padding-right: ${({ icon }) => (icon ? '30px !important' : '0px')};
  &:disabled {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: none;
  }

  &::placeholder {
    color: #555555;
    font-weight: 400;
    opacity: 0.7;
  }

  &[type='checkbox'] {
    min-width: auto;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active,
  &:-webkit-autofill:hover {
    background-color: inherit !important;
    background-clip: content-box;
    -webkit-box-shadow: 0 0 0 30px white inset !important;
    -webkit-text-fill-color: ${({ theme }) => getThemedInputColor(theme.colors.inputColor)};
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const EyeWrapper = styled.div<{ color: string }>`
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  svg {
    width: 20px;
    height: 20px;
    color: ${({ color }) => color};
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  svg {
    width: 20px;
    height: 20px;
  }
`;

type IProps = React.InputHTMLAttributes<HTMLInputElement> & IInputProps;

export const Input: React.FC<IProps> = forwardRef(
  ({ id, label, errorText, className, withEye, type, icon, ...props }, ref?: any) => {
    const [theme] = useTheme();
    const { toggle: isPasswordShowed, handleToggle: togglePasswordShowing } = useToggle();

    // iOS ignores a scripted focus outside a user gesture: no keyboard comes up, but the field
    // still counts as focused, so the user's first real tap changes nothing and raises nothing.
    useEffect(() => {
      if (isTouchDevice()) return;

      ref?.current?.focus();
    }, []);

    const eyePasswordType = isPasswordShowed ? 'text' : 'password';

    const isNewShopPage = window.location.pathname.includes('/shop-new');
    const inputColor = isNewShopPage ? `${theme.colors.black} !important` : theme.colors.inputColor;

    return (
      <FormPart
        id={id}
        label={label}
        className={className}
        isDisabled={props.disabled}
        errorText={errorText}
      >
        <InputWrapper>
          <InputElement
            id={id}
            ref={ref}
            theme={theme}
            className="input-element"
            type={withEye ? eyePasswordType : type}
            icon={icon}
            style={{ color: inputColor }}
            {...props}
          />
          {icon && <IconWrapper>{icon}</IconWrapper>}
          {withEye && (
            <EyeWrapper color={theme.colors.inputColor} onClick={togglePasswordShowing}>
              {isPasswordShowed ? <FaEye /> : <FaEyeSlash />}
            </EyeWrapper>
          )}
        </InputWrapper>
      </FormPart>
    );
  }
);
